import { BreadCrumb } from 'primereact/breadcrumb';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppContext } from '../Context';
import { ApiEndpoints, Constants } from '../constants/appConstants';
import httpService from '../services/httpService';

interface Breadcrumb {
    label: string;
    path?: string;
    url?: string;
}

interface ComponentHeaderProps {
    title?: string;
    breadcrumbs?: Breadcrumb[];
    showProviderTypeDropdown?: boolean;
    hideProviderTypeDropdown?: boolean;
    providerType?: string;
    providerTypeValue?: string;
    onProviderTypeChange?: (value: string) => void;
    onReviewYearChange?: (value: number | undefined) => void;
    reviewYear?: number;
}

type ErrorWithInnerException = Error & {
    innerException?: unknown;
    cause?: unknown;
};

type ReviewYearDetailsResponse = {
    reviewYear?: number;
};

const normalizeProviderType = (value?: string): string => {
    const normalized = String(value || '').trim().toUpperCase();

    if (normalized === Constants.SCOKey) {
        return Constants.SCOKey;
    }

    if (
        normalized === Constants.RSKey ||
        normalized === 'RESIDENTIAL' ||
        normalized === 'RH'
    ) {
        return Constants.RSKey;
    }

    return Constants.RSKey;
};

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return 'Unknown error';
};

const logError = (message: string, error: unknown): void => {
    const typedError = error as ErrorWithInnerException;

    console.error(message, {
        message: getErrorMessage(error),
        innerException: getErrorMessage(
            typedError?.innerException ?? typedError?.cause
        )
    });
};

const ComponentHeader: React.FC<ComponentHeaderProps> = ({
    title,
    breadcrumbs,
    showProviderTypeDropdown,
    hideProviderTypeDropdown = false,
    providerType: providerTypeProp,
    providerTypeValue,
    onProviderTypeChange,
    reviewYear: reviewYearProp,
    onReviewYearChange
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        reviewYear: contextReviewYear,
        providerType: contextProviderType,
        setProviderType,
        setReviewYear,
        providerTypes,
        isLoadingProviderTypes
    } = useAppContext();

    const selectedProviderType =
        providerTypeProp ??
        providerTypeValue ??
        contextProviderType;

    const displayReviewYear =
        reviewYearProp ?? contextReviewYear;

    const shouldShowProviderTypeDropdown =
        !hideProviderTypeDropdown &&
        (showProviderTypeDropdown ?? true);

    const handleHomeNavigation = () => {
        try {
            const normalizedProviderType = normalizeProviderType(
                sessionStorage.getItem('selectedProviderType') ||
                    selectedProviderType
            );

            setProviderType(normalizedProviderType);

            navigate('/pages/home', {
                state: {
                    providerType: normalizedProviderType
                }
            });
        } catch (error: unknown) {
            logError(
                '[ComponentHeader] Error navigating home',
                error
            );
        }
    };

    const home = {
        icon: 'pi pi-home',
        command: handleHomeNavigation
    };

    const handleProviderTypeChange = (value: string) => {
        try {
            const normalizedValue =
                normalizeProviderType(value);

            if (typeof onProviderTypeChange === 'function') {
                onProviderTypeChange(normalizedValue);
                return;
            }

            setProviderType(normalizedValue);

            if (location.pathname === '/dashboard') {
                navigate(
                    `/dashboard?providerType=${normalizedValue}`,
                    {
                        replace: false,
                        state: {
                            providerType: normalizedValue
                        }
                    }
                );
                return;
            }

            if (location.pathname === '/pfp/providers') {
                navigate(
                    `/pfp/providers?providerType=${normalizedValue}`,
                    {
                        replace: false,
                        state: {
                            providerType: normalizedValue
                        }
                    }
                );
            }
        } catch (error: unknown) {
            logError(
                '[ComponentHeader] Error changing provider type',
                error
            );
        }
    };

    useEffect(() => {
        const fetchReviewYear = async (): Promise<void> => {
            if (!selectedProviderType) {
                return;
            }

            try {
                const response = await httpService.get<ReviewYearDetailsResponse>(
                    `${ApiEndpoints.GetCurrentReviewYearDetails}/${selectedProviderType}`
                );

                const reviewYear = response?.data?.reviewYear;

                if (typeof onReviewYearChange === 'function') {
                    onReviewYearChange(reviewYear);
                    return;
                }

                if (typeof reviewYear === 'number') {
                    setReviewYear(reviewYear);
                }
            } catch (error: unknown) {
                logError(
                    '[ComponentHeader] Error fetching review year',
                    error
                );
            }
        };

        void fetchReviewYear();
    }, [
        selectedProviderType,
        onReviewYearChange,
        setReviewYear
    ]);

    const breadcrumbModel = breadcrumbs?.map(
        ({ label, path }) =>
            path
                ? {
                      label,
                      command: () => navigate(path)
                  }
                : {
                      label
                  }
    );

    return (
        <div className="component-header">
            <nav className="breadcrumbs">
                {breadcrumbModel &&
                    breadcrumbModel.length > 0 && (
                        <BreadCrumb
                            model={breadcrumbModel}
                            home={home}
                            style={{
                                background: 'none',
                                border: 'none',
                                paddingLeft: '0px'
                            }}
                        />
                    )}
            </nav>

            <div
                style={{
                    float: 'right',
                    marginTop:
                        breadcrumbs && breadcrumbs.length > 0
                            ? '-30px'
                            : '10px',
                    marginRight: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                }}
            >
                {shouldShowProviderTypeDropdown && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <span style={{ width: '100%' }}>
                            Provider Type:
                        </span>

                        <Dropdown
                            value={selectedProviderType}
                            options={providerTypes}
                            optionLabel="providerType"
                            optionValue="providerCode"
                            onChange={(e) =>
                                handleProviderTypeChange(
                                    e.value
                                )
                            }
                            placeholder="Select Provider Type"
                            loading={isLoadingProviderTypes}
                            style={{ width: '100%' }}
                        />
                    </div>
                )}

                {displayReviewYear && (
                    <div>
                        Review Year: {displayReviewYear}
                    </div>
                )}
            </div>

            {title && (
                <header
                    style={{
                        padding: '5px 5px 5px 5px',
                        fontSize: '25px'
                    }}
                >
                    <strong>{title}</strong>
                </header>
            )}
        </div>
    );
};

export default ComponentHeader;